import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {getActiveMembers} from '../../stores/members'
import {deleteSchedule, getAllMembersSchedule, setIsScheduleLoading, setSchedules} from '../../stores/incident-management'
import {setConfirmIsActive} from '../../stores/alert'
import {DateFormats, formatDate, getValueFromTime, isTimeBefore} from '../../utils/date'
import LimitManager from '../../utils/limitManager'
import Customer from '../../utils/customer'
import {ImgButton} from '../../components/core/button'
import {PageSubTitle, PageTitle, ResponsivePageTitle} from '../../components/core/title'
import Modal from '../../components/core/modal'
import Confirm from '../../components/core/confirm'
import Footer from '../../components/layouts/footer'
import Table from '../../components/core/table'
import CustomScheduleTable from './CustomScheduleTable'
import CreateEditSchedule from './CreateEditSchedule'
import {HorizontalMoreIcon, PlusIcon} from '../../icons'
import './index.scss'
import Dropdown from '../../components/core/dropdown'
import {MenuItem} from '@szhsin/react-menu'
import classNames from 'classnames'
import RenderedForPaid from '../../components/pages/RenderedForPaid'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'

const TIME_TYPE_2_DIGITS = '2-digit'

const {isReadOnlyModeEnabled} = envConfig

const modalTypes = {
    createEditModal: 'create_edit',
    viewScheduleModal: 'view'
}

const OnCallScheduling = () => {
    const dispatch = useDispatch()
    const text = useNamespaceTranslations('on-call-scheduling')
    const usageLimitsData = useNamespaceTranslations('usage-limits')
    const members = useSelector(state => state.members.members)
    const schedules = useSelector(state => state.incident_management.schedules)
    const isDeleting = useSelector(state => state.incident_management.isDeleting)
    const selectedTeamId = useSelector(state => state.teams.selectedTeamId)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const [activeModalType, setActiveModalType] = useState(null)
    const [activeSchedules, setActiveSchedules] = useState([])
    const [deletedSchedule, setDeletedSchedule] = useState({})
    const [updatedSchedule, setUpdatedSchedule] = useState({})
    const [isEdit, setIsEdit] = useState(false)
    const [openMenu, setOpenMenu] = useState(-1)
    const [hasBackOption, sethasBackOption] = useState(false)
    const isOnCallEnabled = LimitManager.getOnCallScheduleIsEnabled(usageLimitsData)
    const neverEndDate = '31.12.9999'

    const activeSchedulesTheader = [
        {
            label: text.time_range
        },
        {
            label: text.start_date_title
        },
        {
            label: text.end_date_title,
            className: 'flex-1'
        }
    ]

    const getActionsMenu = (member, schedule, hasDelete = true, optionalMenuItem, memberIndex = null) => (
        <Dropdown menuButton={<span className="dots-menu"><HorizontalMoreIcon/></span>} setOpenMenu={setOpenMenu} memberIndex={memberIndex} className="on-call-menu">
            {optionalMenuItem && <MenuItem>
                {optionalMenuItem}
            </MenuItem>}
            <MenuItem onClick={() => hasDelete ? handleUpdate(member, schedule) : handleView(member, schedule)} disabled={isReadOnlyModeEnabled}>
                <span className="button-text">{text.edit}</span>
            </MenuItem>
            {hasDelete && <MenuItem onClick={() => handleDelete(member, schedule)} disabled={isReadOnlyModeEnabled}>
                <span className="button-text">{text.delete}</span>
            </MenuItem>}
        </Dropdown>
    )

    const activeSchedulesTbody = activeSchedules?.schedules?.map((currentSchedule) => {
        const startDate = formatDate(
            currentSchedule?.start_date,
            DateFormats.DOT_DMY
        )
        const endDate = formatDate(
            currentSchedule?.end_date,
            DateFormats.DOT_DMY
        )
        const displayEndDate = endDate === neverEndDate ? 'Never' : endDate

        return [
            <span>{currentSchedule?.start_time} - {currentSchedule?.end_time}</span>,
            <span>{startDate}</span>,
            <span className='flex-1'>
                {displayEndDate}
                {getActionsMenu(activeSchedules?.member, currentSchedule)}
            </span>
        ]
    })

    const renderActiveSchedulesList = () => activeSchedules?.schedules?.length > 0 ?
        <Table data={activeSchedules.schedules} tableHeader={activeSchedulesTheader} tbodyData={activeSchedulesTbody}/> : <span className="no-content">{text.no_schedules}</span>

    const calculateLocalHour = (hour, userOffset, hasTimezone) => {
        const localHour = hasTimezone ? parseInt(getValueFromTime(hour, 0)) - userOffset : parseInt(getValueFromTime(hour, 0)) + userOffset

        if (localHour > 24) {
            return localHour % 24
        }

        if (localHour < 0) {
            return localHour + 24
        }

        return localHour
    }

    const getFormattedTimeInTimeZone = (hour, hasTimezone = true) => {
        const utcDate = new Date()
        const userOffset = utcDate.getTimezoneOffset() / 60

        const options = {
            hour: TIME_TYPE_2_DIGITS,
            minute: TIME_TYPE_2_DIGITS,
            hour12: false
        }

        if (!hasTimezone) {
            options.second =  TIME_TYPE_2_DIGITS
        }

        utcDate.setHours(calculateLocalHour(hour, userOffset, hasTimezone), parseInt(getValueFromTime(hour, 1)), !hasTimezone && parseInt(getValueFromTime(hour, 2)))
        return utcDate.toLocaleTimeString('en-US', options)
    }

    const isTimeRangeExceeded = (time1, time2) => isTimeBefore(time1, time2) < 0

    const handleModal = (modalType) => {
        setActiveModalType(activeModalType !== modalType ? modalType : null)
    }

    const handleCloseModal = () => {
        sethasBackOption(false)
        handleModal(activeModalType)
    }

    const fetchTeamMembers = async() => {
        await dispatch(getActiveMembers(selectedTeamId, true))
    }

    const fetchAllMembersSchedule = async() => {
        await dispatch(getAllMembersSchedule(members))
    }

    const deleteSelectedSchedule = async() => {
        await dispatch(deleteSchedule(selectedTeamId, deletedSchedule.team_member_id, deletedSchedule.created_at))
        await dispatch(setConfirmIsActive(false))
        await dispatch(getAllMembersSchedule(members))
        setDeletedSchedule({})
    }

    const addNewSchedule = () => {
        setIsEdit(false)
        handleModal(modalTypes.createEditModal)
    }

    const handleUpdate = (member, schedule) => {
        setIsEdit(true)
        handleModal(modalTypes.createEditModal)
        setUpdatedSchedule({
            ...schedule,
            member: {
                id: member.customer_id,
                label: `${member.name} ${member.surname}`,
                value: `${member.name} ${member.surname}`
            }
        })
    }

    const handleDelete = (member, schedule) => {
        setDeletedSchedule({
            team_member_id: member.customer_id,
            created_at: schedule.created_at
        })
        dispatch(setConfirmIsActive(true))
    }

    const handleView = (member, schedules) => {
        setActiveSchedules({member, schedules})
        handleModal(modalTypes.viewScheduleModal)
        sethasBackOption(true)
    }

    useEffect(() => {
        if (selectedTeamId) {
            fetchTeamMembers()
        }
    }, [selectedTeamId])

    useEffect(() => {
        if (members?.length > 0) {
            fetchAllMembersSchedule()
            return
        }
        dispatch(setSchedules([]))
        dispatch(setIsScheduleLoading(false))
    }, [members])

    return (
        <main className={classNames('main call-scheduling', {
            dark: isDarkMode
        })}>
            <Confirm
                body={text.are_you_sure}
                showLoader={isDeleting}
                callback={deleteSelectedSchedule}
            />
            <Modal contentClassName="overflow-auto" closeModal={handleCloseModal}
                isOpen={!!activeModalType} title={activeModalType === modalTypes.viewScheduleModal ? Customer.getUserFullName(activeSchedules?.member) : text.schedule_modal_title} className="schedule-modal">
                <div className={classNames('schedule', {
                    dark: isDarkMode
                })}>
                    {!isOnCallEnabled && <RenderedForPaid forPaidText={text.for_paid} isExceeded={false}/>}
                    <span className='schedule-desc'>{activeModalType === modalTypes.viewScheduleModal ? text.existing_on_call_desc : text.schedule_modal_desc}</span>
                    {activeModalType === modalTypes.createEditModal &&
                    <CreateEditSchedule
                        handleModal={handleModal}
                        members={members}
                        isEdit={isEdit}
                        updatedSchedule={updatedSchedule}
                        getFormattedTimeInTimeZone={getFormattedTimeInTimeZone}
                        isTimeRangeExceeded={isTimeRangeExceeded}
                        hasBackOption={hasBackOption}
                        isOnCallEnabled={isOnCallEnabled}/>
                    }
                    {activeModalType === modalTypes.viewScheduleModal && renderActiveSchedulesList()}
                </div>
            </Modal>
            <div>
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.section}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                    <ImgButton onClick={addNewSchedule} disabled={isReadOnlyModeEnabled}>
                        {text.add_new_schedule}
                        <span><PlusIcon/></span>
                    </ImgButton>
                </div>
                {<CustomScheduleTable
                    allSchedules={schedules}
                    getFormattedTimeInTimeZone={getFormattedTimeInTimeZone}
                    isTimeRangeExceeded={isTimeRangeExceeded}
                    setActiveSchedules={setActiveSchedules}
                    activeSchedules={activeSchedules}
                    getActionsMenu={getActionsMenu}
                    handleDelete={handleDelete}
                    handleUpdate={handleUpdate}
                    openMenu={openMenu}
                />
                }
            </div>
            <Footer/>
        </main>
    )
}

export default OnCallScheduling
